import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaCheckCircle } from "react-icons/fa";
import { Table, ListGroup, OverlayTrigger, Tooltip } from "react-bootstrap";

import "../styles/Certificaciones.css";
import certificacionesData from "../data/certificacionesData";
import {
  trackCourseDownload,
  trackCourseMoreInfoClick,
} from "../utils/analytics";

const TABLE_COUNT = 3;

const getCourseSlug = (nombreCurso) => {
  if (nombreCurso.includes("EC0616")) return "certificacion-ec0616";
  if (nombreCurso.includes("Auxiliar")) {
    return "curso-auxiliar-enfermeria-ixtapaluca";
  }
  if (nombreCurso.includes("Podolog")) {
    return "diplomado-podologia-clinica-ixtapaluca";
  }
  if (nombreCurso.includes("Fisioterapia")) return "curso-fisioterapia-ixtapaluca";
  if (nombreCurso.includes("Quiro")) return "diplomado-quiropractica-ixtapaluca";
  if (nombreCurso.includes("Masajes")) return "curso-masoterapia-ixtapaluca";
  if (nombreCurso.includes("EC")) return "certificaciones-sep-estado-de-mexico";
  return null;
};

const splitIntoTables = (items) => {
  const chunkSize = Math.ceil(items.length / TABLE_COUNT);

  return Array.from({ length: TABLE_COUNT }, (_, index) =>
    items.slice(index * chunkSize, index * chunkSize + chunkSize)
  ).filter((group) => group.length > 0);
};

function Certificaciones() {
  const tableGroups = useMemo(() => splitIntoTables(certificacionesData), []);
  const [activeTable, setActiveTable] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused || tableGroups.length <= 1) return undefined;

    const timer = window.setInterval(() => {
      setActiveTable((current) => (current + 1) % tableGroups.length);
    }, 7000);

    return () => window.clearInterval(timer);
  }, [isPaused, tableGroups.length]);

  const goToTable = (index) => {
    setActiveTable((index + tableGroups.length) % tableGroups.length);
  };

  const activeItems = tableGroups[activeTable] || [];

  return (
    <section
      id="certificaciones"
      className="certificaciones-container"
      data-aos="fade-right"
    >
      <div className="certificaciones-heading">
        <h2>Certificaciones, cursos y diplomados en Fundacion Nican</h2>
        <p>
          Compara programas en grupos mas ligeros, revisa el temario y entra a
          la pagina especifica del curso que mas se ajuste a tu objetivo
          profesional.
        </p>
      </div>

      <div
        className="certificaciones-carousel"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="certificaciones-carousel-toolbar">
          <button
            className="certificaciones-nav-button"
            type="button"
            onClick={() => goToTable(activeTable - 1)}
            aria-label="Ver tabla anterior"
          >
            Anterior
          </button>

          <div className="certificaciones-carousel-status">
            <strong>Tabla {activeTable + 1}</strong>
            <span>de {tableGroups.length}</span>
          </div>

          <button
            className="certificaciones-nav-button"
            type="button"
            onClick={() => goToTable(activeTable + 1)}
            aria-label="Ver tabla siguiente"
          >
            Siguiente
          </button>
        </div>

        <div className="certificaciones-dots" aria-label="Selector de tablas">
          {tableGroups.map((_, index) => (
            <button
              key={index}
              className={index === activeTable ? "active" : ""}
              type="button"
              onClick={() => goToTable(index)}
              aria-label={`Ver tabla ${index + 1}`}
              aria-current={index === activeTable ? "true" : undefined}
            ></button>
          ))}
        </div>

        <div className="certificaciones-table-shell" key={activeTable}>
          <Table
            striped
            bordered
            hover
            responsive
            className="certificaciones-table"
          >
            <thead>
              <tr>
                <th>Curso | Diplomado | Certificacion</th>
                <th>Temario</th>
                <th>Beneficios y oportunidades</th>
              </tr>
            </thead>
            <tbody>
              {activeItems.map((item) => {
                const courseSlug = getCourseSlug(item.nombre);

                return (
                  <tr key={item.nombre}>
                    <td className="certificacion-name-cell">
                      <strong>{item.nombre}</strong>
                      <div className="certificacion-actions">
                        {courseSlug && (
                          <Link
                            className="certificacion-button certificacion-button-primary"
                            to={`/${courseSlug}`}
                            onClick={() =>
                              trackCourseMoreInfoClick(
                                item.nombre,
                                "tabla_certificaciones"
                              )
                            }
                          >
                            Ver programa
                          </Link>
                        )}

                        {item.descargable && (
                          <button
                            className="certificacion-button certificacion-button-secondary"
                            type="button"
                            onClick={() => {
                              trackCourseDownload(
                                item.nombre,
                                item.descargable,
                                "tabla_certificaciones"
                              );
                              const link = document.createElement("a");
                              link.href = require(`../data/Informacion/${item.descargable}`);
                              link.download = item.descargable;
                              link.click();
                            }}
                          >
                            Descargar PDF
                          </button>
                        )}
                      </div>
                    </td>

                    <td>
                      <ListGroup variant="flush">
                        {item.temario.map((tema, index) => (
                          <OverlayTrigger
                            key={`${item.nombre}-${tema.tema}-${index}`}
                            placement="top"
                            overlay={<Tooltip>{tema.descripcion}</Tooltip>}
                          >
                            <ListGroup.Item className="temario-item">
                              <FaCheckCircle className="temario-icon" />
                              {tema.tema}
                            </ListGroup.Item>
                          </OverlayTrigger>
                        ))}
                      </ListGroup>
                    </td>
                    <td className="certificacion-benefits-cell">
                      {item.beneficios}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </div>
    </section>
  );
}

export default Certificaciones;
